import React, { createContext, useContext, useEffect, useState } from 'react';

interface PWAContextType {
  isInstalled: boolean;
  isStandalone: boolean;
  canInstall: boolean;
  installPrompt: any;
  install: () => Promise<void>;
  updateAvailable: boolean;
  update: () => void;
}

const PWAContext = createContext<PWAContextType | undefined>(undefined);

export const usePWA = () => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
};

export const PWAProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isInstalled, setIsInstalled] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    // Check if app is installed
    const checkInstallation = () => {
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true);
        setIsStandalone(true);
      }
    };

    // Check if app can be installed
    const checkInstallability = () => {
      window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        setInstallPrompt(e);
        setCanInstall(true);
      });
    };

    // Check for updates
    const checkForUpdates = () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          setUpdateAvailable(true);
        });
      }
    };

    checkInstallation();
    checkInstallability();
    checkForUpdates();

    return () => {
      // Cleanup
    };
  }, []);

  const install = async () => {
    if (installPrompt) {
      const result = await installPrompt.prompt();
      if (result.outcome === 'accepted') {
        setIsInstalled(true);
        setCanInstall(false);
      }
    }
  };

  const update = () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration && registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    }
    setUpdateAvailable(false);
  };

  const value: PWAContextType = {
    isInstalled,
    isStandalone,
    canInstall,
    installPrompt,
    install,
    updateAvailable,
    update,
  };

  return <PWAContext.Provider value={value}>{children}</PWAContext.Provider>;
}; 